import type { ProductDetail } from '../../domain/models/ProductDetail';
import type { PdpEffectiveProductVm } from '../viewModels/pdp.vm';
import { normalizeNumericField } from '@/features/pdp/utils/normalizeNumericField';
import { toPriceNumber, type PriceInput } from '@/shared/utils/priceFormatter';

/** First parseable price as a finite number. WebEngage Number attrs become null for NaN/strings. */
export function resolvePdpAnalyticsPrice(...sources: PriceInput[]): number {
  for (const source of sources) {
    const parsed = toPriceNumber(source);
    if (parsed != null) return parsed;
  }
  return 0;
}

/**
 * Selling price shown on PDP (`discountedPrice ?? price`).
 * Most products have MRP but no separate discountedPrice — sending only discountedPrice
 * made WebEngage `Selling Price` null/0.
 */
export function resolvePdpSellingPrice(
  vm: PdpEffectiveProductVm,
  product?: ProductDetail,
): number {
  return resolvePdpAnalyticsPrice(
    vm.discountedPrice,
    vm.price,
    product?.discountedPrice,
    product?.price,
  );
}

function getImageUrlFromMediaItems(mediaItems: Record<string, unknown>): string {
  const carouselSeq = (mediaItems as { carouselSeq?: unknown }).carouselSeq;
  if (!Array.isArray(carouselSeq)) return '';
  for (const item of carouselSeq) {
    if (!item || typeof item !== 'object') continue;
    const typed = item as { type?: string; urlMap?: Record<string, string> };
    if (typed.type !== 'image' || !typed.urlMap) continue;
    return typed.urlMap['1024'] ?? typed.urlMap['828'] ?? typed.urlMap['70'] ?? '';
  }
  return '';
}

export function getFirstMediaImageUrl(mediaItems: Record<string, unknown>): string {
  const carouselSeq = (mediaItems as { carouselSeq?: unknown }).carouselSeq;
  if (!Array.isArray(carouselSeq) || carouselSeq.length === 0) return '';
  const first = carouselSeq[0] as { urlMap?: Record<string, string> } | undefined;
  return first?.urlMap?.['70'] ?? '';
}

/**
 * Builds the standard product attribute map used across PDP WebEngage events.
 * Mirrors getProductAttributes() from the old app's eventLogger.js.
 */
export function buildPdpProductAttributes(
  product: ProductDetail,
  vm: PdpEffectiveProductVm,
  selectedSizeDisplay?: string,
): Record<string, unknown> {
  const imageUrl = getImageUrlFromMediaItems(product.mediaItems);

  const stonesArr: string[] = [];
  if (vm.diamondStoneType) stonesArr.push('Diamond');
  const solitaireCarat = vm.solitaireCarat ?? product.solitaireCarat;
  if (solitaireCarat) stonesArr.push('Solitaire');
  if (product.gemstones) stonesArr.push(product.gemstones);
  const stonesName = stonesArr.join(',');

  const attrs: Record<string, unknown> = {
    'Product Id': String(product.designId),
    'Customization Id': String(vm.activeCustomizationId),
    'Product Name': product.designName ?? '',
    'Category Name': product.category ?? '',
    'MRP': resolvePdpAnalyticsPrice(vm.price, product.price),
    'Selling Price': resolvePdpSellingPrice(vm, product),
    'Discount': resolvePdpAnalyticsPrice(vm.discountValue, product.discountValue),
    'Offer Name': vm.discountName ?? '',
    'Collection Name': product.collectionName ?? '',
    'Color': vm.metal ?? '',
    'Image': imageUrl,
    'Metal Type': vm.metalType ?? '',
    'Metal Purity': vm.goldPurity ?? '',
    'Currency': 'INR',
    'Error Flag': !selectedSizeDisplay,
    'Size': selectedSizeDisplay ?? '',
  };

  // Number attrs: never fall back to '' — WebEngage locks the attribute's data
  // type from the first value it receives, and a stray string permanently
  // nulls out every future numeric value for that attribute.
  const metalWeight = normalizeNumericField(vm.metalWeight);
  if (metalWeight != null) attrs['Metal Weight'] = metalWeight;

  const productWeight = normalizeNumericField(vm.productWeight);
  if (productWeight != null) attrs['Product Weight'] = productWeight;

  if (product.productHeight != null) {
    const height = normalizeNumericField(product.productHeight);
    if (height != null) attrs['Height'] = height;
  }
  if (product.productWidth != null) {
    const width = normalizeNumericField(product.productWidth);
    if (width != null) attrs['Width'] = width;
  }
  // Length is registered in WebEngage as a String attribute (legacy app sent
  // the raw unparsed value) — keep sending a string so values aren't dropped.
  if (product.productLength != null) {
    const length = normalizeNumericField(product.productLength);
    if (length != null) attrs['Length'] = String(length);
  }
  if (product.diamondCarat != null) attrs['Diamond Weight'] = product.diamondCarat;
  if (vm.diamondStoneType) attrs['Diamond Color'] = vm.diamondStoneType;
  if (product.noOfDiamonds != null) attrs['No. of Diamonds'] = product.noOfDiamonds;
  if (stonesName) attrs['Stones'] = stonesName;
  if (product.gemstonesTypeAndCount) attrs['No. of other stones'] = product.gemstonesTypeAndCount;
  if (product.solitaireClarity) attrs['Solitaire Clarity'] = product.solitaireClarity;
  if (product.solitaireColor) attrs['Solitaire Color'] = product.solitaireColor;
  if (solitaireCarat) attrs['Solitaire Weight'] = solitaireCarat;
  if (product.solitaireTotalNoOfStones != null) attrs['No. of Solitaire'] = product.solitaireTotalNoOfStones;

  return attrs;
}
